import { useCallback, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { tooltip, type TooltipId } from "../lib/tooltips";

/** Space kept between the "?" and its popup, and between the popup and the window's edge. */
const GAP = 6;
const EDGE = 8;

/**
 * Whether the focus now arriving came from Tab. A hint opens on keyboard
 * focus, but not on focus a script moves (a dialog opening) or a pointer
 * lands (a press on the "?", which the click handles) - #420. The last
 * Tab keydown or pointerdown decides; nothing else touches it.
 */
let tabbing = false;
if (typeof document !== "undefined") {
  document.addEventListener("keydown", (e) => (tabbing = e.key === "Tab"), true);
  document.addEventListener("pointerdown", () => (tabbing = false), true);
  document.addEventListener("mousedown", () => (tabbing = false), true);
}

/**
 * A small "?" beside a term that shows its line from the tooltip register
 * on hover, on a click or tap, and on keyboard focus - only when asked
 * (#420): a dialog that opens with focus near it does not open it. The text is also the control's accessible
 * description, so a screen reader hears it without the pointer.
 *
 * The popup is fixed to the window, not placed inside the page (#401): an
 * absolutely placed popup lay over the control the "?" labels and, inside
 * a dialog, over its buttons, and made the dialog scroll to fit it. It
 * opens above the "?" - what a label describes sits below or beside it -
 * and below where there is no room above. Room ends at the window's top
 * and at the frozen page header's bottom edge (#486: a hint in a profile's
 * bar opened under the header and read from mid-sentence). The popup lives
 * on document.body - or in the open dialog, which is in the top layer - so
 * no sticky band's stacking context can hide it.
 *
 * `more` adds a sentence only the page can know (#495: which of the
 * organization's environments share this AWS account) after the register's line.
 */
export function Hint({ id, label, more }: { id: TooltipId; label?: string; more?: string | undefined }) {
  const text = tooltip(id, more);
  const descId = useId();
  // Each way of asking is held apart, so leaving one does not undo another: a tap survives the pointer moving off.
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [keyed, setKeyed] = useState(false);
  const open = hovered || pinned || keyed;
  const close = () => {
    setHovered(false);
    setPinned(false);
    setKeyed(false);
  };
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const popup = useRef<HTMLSpanElement>(null);

  // A modal dialog sits in the top layer, above anything on body, so a "?" inside one keeps its popup there.
  const [host, setHost] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => setHost(button.current?.closest("dialog") ?? document.body), []);

  const place = useCallback(() => {
    const el = button.current;
    const p = popup.current?.getBoundingClientRect();
    if (!el || !p) return;
    const b = el.getBoundingClientRect();
    const ceiling = Math.max(EDGE, frozenBottom(el) + GAP);
    const above = b.top - GAP - p.height;
    const below = b.bottom + GAP;
    const fitsAbove = above >= ceiling;
    const fitsBelow = below + p.height <= window.innerHeight - EDGE;
    // Room on neither side: whichever has more, and never under the frozen header.
    const top = fitsAbove ? above : fitsBelow || window.innerHeight - b.bottom >= b.top - ceiling ? below : ceiling;
    const left = Math.min(Math.max(b.left + b.width / 2 - p.width / 2, EDGE), window.innerWidth - p.width - EDGE);
    setAt({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setAt(null);
      return;
    }
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, place]);

  const tip = (
    <span
      ref={popup}
      id={descId}
      role="tooltip"
      // Coordinates are measured, so they cannot be classes; everything else is.
      style={open && at ? { top: at.top, left: at.left } : undefined}
      // Closed, it is only the "?"'s description: sr-only and nothing else, or its width still widens the page (#401).
      // z-[60]: above the frozen bands (PageHeader z-20, the flow bar z-10) and the rail's drawer.
      className={open ? `fixed z-[60] ${at ? "" : "invisible left-0 top-0"} pointer-events-none w-64 max-w-[calc(100vw-1rem)] rounded-lg border border-neutral-200 bg-white p-2.5 text-left text-xs font-normal normal-case tracking-normal text-neutral-800 shadow-lg dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100` : "sr-only"}
    >
      {text}
    </span>
  );

  return (
    <span className="relative inline-block align-middle">
      <button
        ref={button}
        data-hint=""
        type="button"
        aria-label={label ? `About ${label}` : "More about this"}
        aria-describedby={descId}
        aria-expanded={open}
        // A click opens it, and a second click closes it - hover had already opened it before the first.
        onClick={() => (pinned ? close() : setPinned(true))}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => tabbing && setKeyed(true)}
        onBlur={() => {
          setPinned(false);
          setKeyed(false);
        }}
        onKeyDown={(e) => {
          if (e.key !== "Escape" || !open) return;
          // The first Escape closes the hint alone, not the dialog around it.
          e.preventDefault();
          e.stopPropagation();
          close();
        }}
        className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full border border-neutral-400 text-[10px] font-semibold leading-none text-neutral-500 hover:border-ink hover:text-ink dark:border-neutral-600 dark:text-neutral-400 dark:hover:text-white"
      >
        ?
      </button>
      {host ? createPortal(tip, host) : tip}
    </span>
  );
}

/**
 * Where the frozen page header (PageHeader, index.css "Frozen bands") ends
 * in the window: its measured bottom, or --page-bar where it is frozen but
 * not found. Nothing covers a "?" in the header itself or in a dialog.
 */
function frozenBottom(el: HTMLElement): number {
  if (el.closest("dialog")) return 0;
  const header = document.querySelector<HTMLElement>("[data-page-bar]");
  if (header?.contains(el)) return 0;
  const bar = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--page-bar")) || 0;
  return Math.max(bar, header?.getBoundingClientRect().bottom ?? 0);
}
